import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, DollarSign, TrendingUp, Info, CheckCircle2, Search, AlertTriangle, ClipboardList } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { Card, CircularProgress, ProgressBar, PageHeader } from '../components/Card';

const journeySteps = [
  'Start Saving', 'Pre-Approval', 'House Hunting', 'Offer Accepted',
  'Home Inspection', 'Financing', 'Closing Day', 'Move-In'
];

export default function Dashboard() {
  const navigate = useNavigate();
  const {
    budget, settings, timeline, setTimeline, getMonthlyPayment, getDownPaymentAmount,
    getTotalDownPaymentSources, getTotalMonthlyCost, getInspectionStats,
    getTaskStats, getClosingCosts, documents,
  } = useApp();

  const downPayment = getDownPaymentAmount();
  const totalSources = getTotalDownPaymentSources();
  const downPaymentPct = downPayment > 0 ? Math.round((totalSources / downPayment) * 100) : 0;
  const inspStats = getInspectionStats();
  const inspPct = inspStats.total > 0 ? Math.round((inspStats.reviewed / inspStats.total) * 100) : 0;
  const taskStats = getTaskStats();
  const monthlyPayment = Math.round(getMonthlyPayment());
  const totalMonthly = getTotalMonthlyCost();
  const closingCosts = getClosingCosts();

  const overallPct = Math.round(
    ((timeline.currentStage - 1) / (journeySteps.length - 1)) * 100 * 0.4 +
    (inspPct * 0.3) +
    (taskStats.total > 0 ? (taskStats.completed / taskStats.total) * 100 * 0.3 : 0)
  );

  const affordability = Math.round((budget.annualIncome || 157000) * 4.5);

  return (
    <div>
      <PageHeader
        icon={<Home size={22} />}
        title="Dashboard"
        subtitle="Your complete guide to buying your first home in Canada"
      />

      {/* Top Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 16 }}>
        <Card dark>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <Home size={20} color="var(--primary)" />
          </div>
          <div style={{ fontSize: 13, opacity: 0.7 }}>Target Home Price</div>
          <div style={{ fontSize: 32, fontWeight: 700 }}>${budget.purchasePrice.toLocaleString()}</div>
          <div style={{ fontSize: 13, opacity: 0.6 }}>{settings.city}, {settings.province}</div>
        </Card>

        <Card dark style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Down Payment Progress</div>
            <div style={{ fontSize: 28, fontWeight: 700, margin: '4px 0' }}>${totalSources.toLocaleString()}</div>
            <div style={{ fontSize: 13, opacity: 0.6 }}>of ${downPayment.toLocaleString()}</div>
          </div>
          <CircularProgress value={Math.min(downPaymentPct, 100)} color="var(--green)" />
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 32 }}>
        <Card dark>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <DollarSign size={20} color="var(--primary)" />
          </div>
          <div style={{ fontSize: 13, opacity: 0.7 }}>Est. Monthly Cost</div>
          <div style={{ fontSize: 32, fontWeight: 700 }}>${totalMonthly.toLocaleString()}</div>
          <div style={{ fontSize: 13, opacity: 0.6 }}>Mortgage + taxes + insurance</div>
        </Card>

        <Card dark style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Inspection Progress</div>
            <div style={{ fontSize: 28, fontWeight: 700, margin: '4px 0' }}>{inspStats.reviewed}/{inspStats.total}</div>
            <div style={{ fontSize: 13, opacity: 0.6 }}>items reviewed</div>
          </div>
          <CircularProgress value={inspPct} color="var(--green)" />
        </Card>
      </div>

      {/* Home Buying Journey */}
      <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 16 }}>Your Home Buying Journey</h2>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--gray-600)' }}>Offer Acceptance Date</label>
            <input type="date" value={timeline.offerAcceptanceDate}
              onChange={e => setTimeline(prev => ({ ...prev, offerAcceptanceDate: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 13, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--gray-600)' }}>Condition Removal Date</label>
            <input type="date" value={timeline.conditionRemovalDate}
              onChange={e => setTimeline(prev => ({ ...prev, conditionRemovalDate: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 13, marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 500, color: 'var(--gray-600)' }}>Target Closing Date</label>
            <input type="date" value={timeline.targetClosingDate}
              onChange={e => setTimeline(prev => ({ ...prev, targetClosingDate: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius-sm)', fontSize: 13, marginTop: 4 }} />
          </div>
        </div>

        {/* Journey Steps */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', padding: '0 8px' }}>
          <div style={{
            position: 'absolute', top: '50%', left: 24, right: 24,
            height: 3, background: 'var(--gray-200)', transform: 'translateY(-50%)', zIndex: 0,
          }}>
            <div style={{
              height: '100%',
              width: `${((timeline.currentStage - 1) / (journeySteps.length - 1)) * 100}%`,
              background: 'var(--green)', borderRadius: 3,
            }} />
          </div>
          {journeySteps.map((step, i) => {
            const stepNum = i + 1;
            const isCompleted = stepNum < timeline.currentStage;
            const isCurrent = stepNum === timeline.currentStage;
            return (
              <div key={step}
                onClick={() => setTimeline(prev => ({ ...prev, currentStage: stepNum }))}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', zIndex: 1, flex: 1, cursor: 'pointer' }}>
                <div style={{
                  width: 36, height: 36, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 600,
                  background: isCompleted ? 'var(--green)' : isCurrent ? 'white' : 'var(--gray-100)',
                  color: isCompleted ? 'white' : isCurrent ? 'var(--green)' : 'var(--gray-500)',
                  border: isCurrent ? '2px solid var(--green)' : isCompleted ? 'none' : '1px solid var(--gray-300)',
                  transition: 'all 0.2s',
                }}>
                  {isCompleted ? <CheckCircle2 size={18} /> : stepNum}
                </div>
                <span style={{
                  fontSize: 10, marginTop: 6, textAlign: 'center',
                  color: isCompleted || isCurrent ? 'var(--dark)' : 'var(--gray-500)',
                  fontWeight: isCurrent ? 600 : 400,
                }}>{step}</span>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Overall Progress */}
      <Card dark style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <TrendingUp size={20} />
          <h3 style={{ fontSize: 18, fontWeight: 600 }}>Overall Progress</h3>
        </div>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <div style={{ fontSize: 48, fontWeight: 700, color: 'var(--primary)' }}>{overallPct}%</div>
          <div style={{ fontSize: 14, opacity: 0.7 }}>Complete</div>
        </div>
        <ProgressBar value={overallPct} color="var(--green)" height={10} style={{ marginBottom: 16 }} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div><span style={{ fontSize: 13, opacity: 0.7 }}>Documents</span><div style={{ fontSize: 18, fontWeight: 600 }}>{documents.length}</div></div>
          <div><span style={{ fontSize: 13, opacity: 0.7 }}>Inspections</span><div style={{ fontSize: 18, fontWeight: 600 }}>{inspStats.reviewed}/{inspStats.total}</div></div>
          <div><span style={{ fontSize: 13, opacity: 0.7 }}>Move-in Tasks</span><div style={{ fontSize: 18, fontWeight: 600 }}>{taskStats.completed}/{taskStats.total}</div></div>
          <div><span style={{ fontSize: 13, opacity: 0.7 }}>Timeline</span><div style={{ fontSize: 18, fontWeight: 600 }}>Stage {timeline.currentStage}/{journeySteps.length}</div></div>
        </div>
      </Card>

      {/* Info Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
        <Card style={{ borderLeft: '4px solid var(--primary)', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <TrendingUp size={16} color="var(--primary)" />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Affordability Estimate</span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>${affordability.toLocaleString()}</div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Based on ${(budget.annualIncome || 157000).toLocaleString()} household income with stress test</div>
        </Card>
        <Card style={{ borderLeft: '4px solid var(--blue)', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Info size={16} color="var(--blue)" />
            <span style={{ fontSize: 13, fontWeight: 500 }}>Estimated Closing Costs</span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>${closingCosts.toLocaleString()}</div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>Budget approximately 3% of purchase price</div>
        </Card>
        <Card style={{ borderLeft: `4px solid ${inspStats.poor > 0 ? 'var(--red)' : inspStats.fair > 0 ? 'var(--yellow)' : 'var(--green)'}`, padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            {inspStats.poor > 0 ? <AlertTriangle size={16} color="var(--red)" /> : <CheckCircle2 size={16} color="var(--green)" />}
            <span style={{ fontSize: 13, fontWeight: 500 }}>Inspection Alerts</span>
          </div>
          <div style={{ fontSize: 22, fontWeight: 700 }}>
            {inspStats.poor > 0
              ? `${inspStats.poor} issue${inspStats.poor > 1 ? 's' : ''} found`
              : inspStats.fair > 0
              ? `${inspStats.fair} minor concern${inspStats.fair > 1 ? 's' : ''}`
              : 'No active issues'}
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
            {inspStats.poor > 0
              ? 'Review and address before closing'
              : inspStats.fair > 0
              ? 'Worth monitoring'
              : 'Looking good so far!'}
          </div>
        </Card>
      </div>

      {/* Quick Nav Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {[
          { icon: <DollarSign size={24} />, title: 'Budget Planner', desc: 'Track savings & calculate affordability', path: '/budget' },
          { icon: <Search size={24} />, title: 'Home Inspection', desc: 'Complete your inspection checklist', path: '/inspection' },
          { icon: <AlertTriangle size={24} />, title: 'Red Flags', desc: 'Track issues & negotiate repairs', path: '/inspection' },
          { icon: <ClipboardList size={24} />, title: 'Reports & Timeline', desc: 'View your complete journey', path: '/reports' },
        ].map(item => (
          <Card key={item.title} dark
            onClick={() => navigate(item.path)}
            style={{ cursor: 'pointer', borderLeft: '4px solid var(--primary)' }}
          >
            <div style={{ color: 'var(--primary)', marginBottom: 10 }}>{item.icon}</div>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{item.title}</div>
            <div style={{ fontSize: 13, opacity: 0.7 }}>{item.desc}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}
